// src/modules/competitions/pages/JudgeEvaluations.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import JudgeImageTypeCard from '../components/JudgeImageTypeCard';
import JudgeEvaluationModal from '../components/JudgeEvaluationModal';
import { judgeService } from '../services/judgeService';
import { JudgeImageType, CategoryJudgeStatus } from '../types/judge';
import '../styles/judgeEvaluations.css';

const JudgeEvaluations: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [eventTitle, setEventTitle] = useState('');
  const [categories, setCategories] = useState<CategoryJudgeStatus[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [imageTypes, setImageTypes] = useState<JudgeImageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyingLevel, setApplyingLevel] = useState<string | null>(null);

  // Modal de evaluación
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState<{
    id: number;
    name: string;
  } | null>(null);

  useEffect(() => {
    if (eventId) {
      loadJudgeStatus();
    }
  }, [eventId]);

  useEffect(() => {
    if (selectedCategory && selectedLevel) {
      loadImageTypes();
    }
  }, [selectedCategory, selectedLevel]);

  const loadJudgeStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await judgeService.getStatusByEvent(Number(eventId));
      
      if (response.success && response.data) {
        setEventTitle(response.data.event_title);
        setCategories(response.data.categories);
        
        if (response.data.categories.length === 0) {
          setError('No hay categorías disponibles en esta competencia');
          return;
        }
        
      } else {
        setError(response.message || 'Error al cargar estado');
      }
    } catch (err) {
      setError('Error al cargar información del juez');
      console.error('Error en loadJudgeStatus:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadImageTypes = async () => {
    if (!selectedCategory || !selectedLevel) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await judgeService.getImageTypes(
        selectedCategory,
        selectedLevel
      );
      
      if (response.success && response.data) {
        setImageTypes(response.data.image_types);
      } else {
        setError(response.message || 'Error al cargar tipos de imagen');
      }
    } catch (err) {
      setError('Error al cargar tipos de imagen');
      console.error('Error en loadImageTypes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryImageUrl = (categoryId: number): string => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://www.ibloom.cl/api';
    return `${API_BASE}/api/v1/competitions/category-image.php?id=${categoryId}`;
  };

  const getLevelStatus = (
    category: CategoryJudgeStatus,
    level: string
  ): 'not_applied' | 'pending' | 'approved' | 'rejected' => {
    if (!category.level_applications) return 'not_applied';

    const app = category.level_applications.find(la => la.level === level);
    if (!app) return 'not_applied';

    return app.approval_status as 'pending' | 'approved' | 'rejected';
  };

  const handleApplyToLevel = async (categoryId: number, level: string) => {
    const category = categories.find(c => c.category_id === categoryId);
    if (!category) {
      setError('Error: No se encontró la categoría');
      return;
    }

    try {
      setApplyingLevel(`${categoryId}-${level}`);
      setError(null);

      let judgeId = category.judge_id;

      if (!judgeId) {
        const termsResponse = await judgeService.getTerms(Number(eventId));
        
        if (!termsResponse.success || !termsResponse.data) {
          setError('No se pudieron cargar los términos');
          return;
        }

        const applyResponse = await judgeService.apply({
          category_id: categoryId,
          terms_id: termsResponse.data.id
        });

        if (!applyResponse.success || !applyResponse.data?.judge_id) {
          setError(applyResponse.message || 'Error al postular a la categoría');
          return;
        }

        judgeId = applyResponse.data.judge_id;
      }

      const levelResponse = await judgeService.applyToLevels({
        judge_id: judgeId,
        levels: [level]
      });

      if (levelResponse.success) {
        await loadJudgeStatus();
      } else {
        setError(levelResponse.message || 'Error al postular al nivel');
      }
    } catch (err) {
      setError('Error al enviar postulación');
      console.error('Error en handleApplyToLevel:', err);
    } finally {
      setApplyingLevel(null);
    }
  };

  // Reemplazar el método handleEvaluateLevel en JudgeEvaluations.tsx

const handleEvaluateLevel = (categoryId: number, level: string) => {
  // ✅ NUEVO: Navegar a la vista de participantes pasando datos por state
  navigate(`/competitions/judge/${eventId}/evaluations`, {
    state: {
      categoryId,
      level,
      categoryName: categories.find(c => c.category_id === categoryId)?.category_name
    }
  });
};

  const handleEvaluateImageType = (imageTypeId: number) => {
    const imageType = imageTypes.find(it => it.image_type_id === imageTypeId);
    if (imageType) {
      setSelectedImageType({
        id: imageTypeId,
        name: imageType.image_type_name
      });
      setShowEvaluationModal(true);
    }
  };

  const handleEvaluationComplete = () => {
    loadImageTypes();
  };

  if (isLoading && categories.length === 0) {
    return <Loading message="Cargando panel de juez..." />;
  }

  return (
    <div className="judge-evaluations-page">
      <Card variant="glass" className="judge-header">
        <h1>Panel de Juez</h1>
        <h2>{eventTitle}</h2>
        
        {error && <ErrorMessage message={error} type="error" onClose={() => setError(null)} />}
      </Card>

      {/* Grid de categorías con estilo imagen */}
      {!selectedCategory && (
        <div className="judge-categories-grid">
          {categories.map(category => (
            <div
              key={category.category_id}
              className="judge-category-card"
            >
              <img
                src={getCategoryImageUrl(category.category_id)}
                alt={category.category_name}
                className="judge-category-bg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.background = 'linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%)';
                  }
                }}
              />

              <div className="judge-category-overlay"></div>

              <div className="judge-category-content">
                <div className="judge-category-header">
                  <span className="judge-role-badge">JUEZ</span>
                  <h3 className="judge-category-title">{category.category_name}</h3>
                </div>

                <div className="judge-category-footer">
                  <div className="judge-levels-section">
                    <span className="judge-levels-label">Niveles disponibles:</span>
                    
                    <div className="judge-levels-grid">
                      {category.available_levels.map(level => {
                        const status = getLevelStatus(category, level);
                        const isApplying = applyingLevel === `${category.category_id}-${level}`;

                        return (
                          <div key={level} className="judge-level-box">
                            {status === 'not_applied' && (
                              <button
                                className="judge-level-btn judge-btn-apply"
                                onClick={() => handleApplyToLevel(category.category_id, level)}
                                disabled={isApplying}
                              >
                                {isApplying ? (
                                  <>
                                    <span className="judge-spinner"></span>
                                    Postulando...
                                  </>
                                ) : (
                                  <>Postular {level}</>
                                )}
                              </button>
                            )}

                            {status === 'pending' && (
                              <div className="judge-level-badge judge-badge-pending">
                                <span className="judge-badge-icon">⏱️</span>
                                <span>{level} - Pendiente</span>
                              </div>
                            )}

                            {status === 'rejected' && (
                              <div className="judge-level-badge judge-badge-rejected">
                                <span className="judge-badge-icon">❌</span>
                                <span>{level} - Rechazado</span>
                              </div>
                            )}

                            {status === 'approved' && (
                              <button
                                className="judge-level-btn judge-btn-evaluate"
                                onClick={() => handleEvaluateLevel(category.category_id, level)}
                              >
                                <span className="judge-check-icon">✓</span>
                                Evaluar {level}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grid de tipos de imagen con ordenamiento */}
      {selectedCategory && selectedLevel && (
        <>
          <Card variant="glass" className="judge-navigation-card">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedCategory(null);
                setSelectedLevel('');
                setImageTypes([]);
              }}
            >
              ← Volver a categorías
            </Button>
            <h3 className="judge-evaluation-title">
              Evaluando: {categories.find(c => c.category_id === selectedCategory)?.category_name} - {selectedLevel}
            </h3>
          </Card>

          {isLoading ? (
            <Loading message="Cargando imágenes..." />
          ) : imageTypes.length === 0 ? (
            <Card variant="flat">
              <p>No hay imágenes para evaluar en este nivel</p>
            </Card>
          ) : (
            <div className="judge-instagram-grid">
              {imageTypes.map(imageType => (
                <JudgeImageTypeCard
                  key={imageType.image_type_id}
                  imageType={imageType}
                  onEvaluate={handleEvaluateImageType}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal de evaluación */}
      {selectedImageType && selectedCategory && selectedLevel && (
        <JudgeEvaluationModal
          isOpen={showEvaluationModal}
          onClose={() => {
            setShowEvaluationModal(false);
            setSelectedImageType(null);
          }}
          categoryId={selectedCategory}
          level={selectedLevel}
          imageTypeId={selectedImageType.id}
          imageTypeName={selectedImageType.name}
          onEvaluationComplete={handleEvaluationComplete}
        />
      )}
    </div>
  );
};

export default JudgeEvaluations;